/**
 * Chart-asset API — drawings, templates, chart settings profiles.
 *
 * `/v2/chart-assets/{namespace}/{asset_type}/{asset_id}` — single generic
 * CRUD over a namespaced per-record store. See `chart_assets_service.py`.
 * Moved out of `workspace/workspace-api.ts`: chart assets are chart-owned
 * and have no dependency on workspace state.
 *
 * Namespacing:
 *   - drawings: namespace = page scope (`dashboard`, `history`, `research`).
 *     asset_id = `${ticker}__${syncId}`; per-ticker scoping via
 *     `?id_prefix=${ticker}__`.
 *   - templates + chart settings profiles: namespace = `'global'` today.
 *     Every function takes a namespace param (defaulting to global) so a
 *     future per-page split needs no plumbing changes here.
 *
 * Every consumer is a one-liner over `usersClient`. Auth + 401-refresh are
 * shared session-wide via `services/api/api-client.ts`.
 */

import { usersClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'
import { recordDump } from '@/services/api/save-dump'
import type { TemplateData, SavedDrawing } from '@/workspace/types'

export const GLOBAL_NAMESPACE = 'global'

export type ChartAssetType = 'drawing' | 'template' | 'chartSettingsProfile' | 'chartPreferences'

function chartAssetPath(namespace: string, assetType: ChartAssetType, assetId?: string) {
  const base = `/v2/chart-assets/${encodeURIComponent(namespace)}/${assetType}`
  return assetId ? `${base}/${encodeURIComponent(assetId)}` : base
}

/** Drawings store ticker inside the asset_id so per-ticker scoping rides on `?id_prefix=`. */
const TICKER_ID_SEPARATOR = '__'

export function makeDrawingAssetId(ticker: string, syncId: string): string {
  return `${ticker}${TICKER_ID_SEPARATOR}${syncId}`
}

/** Inverse of {@link makeDrawingAssetId}; returns `[ticker, syncId]` or null on malformed input. */
function parseDrawingAssetId(assetId: string): [string, string] | null {
  const idx = assetId.indexOf(TICKER_ID_SEPARATOR)
  if (idx === -1) return null
  return [assetId.slice(0, idx), assetId.slice(idx + TICKER_ID_SEPARATOR.length)]
}

// ─── Templates ───
//
// The frontend mints the id on save — server-generated ids are a future
// tweak (see comment in chart-panel handleSaveTemplate).

function makeTemplateId(): string {
  return `tpl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export async function loadTemplates(namespace = GLOBAL_NAMESPACE): Promise<TemplateData[]> {
  // `full=true` returns complete records in the ONE list Query, the same way
  // drawings load. This used to fetch summaries and then issue a per-id GET for
  // the content it had just declined — 1 + N requests per page mount.
  //
  // The projection was never the win: DynamoDB applies ProjectionExpression
  // AFTER reading the item, so summary mode costs identical RCU and only trims
  // the response body. What it did cost was N extra round trips, each paying
  // auth + middleware + a GetItem (+ a CORS preflight in staging, since every
  // distinct URL caches its own). Those saturated the connection pool and
  // starved the concurrent drawings fetch.
  const items = await usersClient.get<Array<Record<string, unknown>>>(
    `${chartAssetPath(namespace, 'template')}?full=true`,
  )
  if (!Array.isArray(items)) return []
  return items.map((item) => ({ ...item, templateId: String(item.id) }) as TemplateData)
}

export async function saveTemplate(
  data: Omit<TemplateData, 'templateId' | 'createdAt' | 'updatedAt'>,
  namespace = GLOBAL_NAMESPACE,
): Promise<TemplateData> {
  const templateId = makeTemplateId()
  const path = chartAssetPath(namespace, 'template', templateId)
  recordDump(namespace, 'saveTemplate', path, { templateId, ...data })
  const saved = await usersClient.put<Record<string, unknown>>(path, data)
  return { ...saved, templateId } as TemplateData
}

/** Overwrite an existing template in place: same asset id, so the PUT replaces it. */
export async function updateTemplate(
  templateId: string,
  data: Omit<TemplateData, 'templateId' | 'createdAt' | 'updatedAt'>,
  namespace = GLOBAL_NAMESPACE,
): Promise<TemplateData> {
  const path = chartAssetPath(namespace, 'template', templateId)
  recordDump(namespace, 'updateTemplate', path, { templateId, ...data })
  const saved = await usersClient.put<Record<string, unknown>>(path, data)
  return { ...saved, templateId } as TemplateData
}

export async function deleteTemplate(
  templateId: string,
  namespace = GLOBAL_NAMESPACE,
): Promise<void> {
  const path = chartAssetPath(namespace, 'template', templateId)
  recordDump(namespace, 'deleteTemplate', path, { templateId })
  await usersClient.delete(path)
}

// ─── Drawings ───
//
// Drawings live under namespace = page scope. asset_id is
// `${ticker}__${syncId}` so a list with `?id_prefix=${ticker}__` returns
// only the drawings for that ticker, with full content (no projection).

export async function loadDrawings(namespace: string, ticker: string): Promise<SavedDrawing[]> {
  const items = await usersClient.get<Array<Record<string, unknown>>>(
    `${chartAssetPath(namespace, 'drawing')}?id_prefix=${encodeURIComponent(
      `${ticker}${TICKER_ID_SEPARATOR}`,
    )}&full=true`,
  )
  if (!Array.isArray(items)) return []
  return items
    .map((item) => {
      const assetId = String(item.id ?? '')
      const parsed = parseDrawingAssetId(assetId)
      if (!parsed) return null
      const [, syncId] = parsed
      const drawing: SavedDrawing = {
        syncId,
        name: String(item.name ?? ''),
        points: (item.points as SavedDrawing['points']) ?? [],
      }
      // Optional style / visibility / lock — absent on legacy records.
      if (item.styles !== undefined) drawing.styles = item.styles as SavedDrawing['styles']
      if (typeof item.visible === 'boolean') drawing.visible = item.visible
      if (typeof item.lock === 'boolean') drawing.lock = item.lock
      if (item.extendData !== undefined) drawing.extendData = item.extendData
      return drawing
    })
    .filter((d): d is SavedDrawing => d !== null)
}

/** The record as it goes over the wire. One builder, so the tab-close drain
 *  can never drift from the normal save. */
function drawingBody(drawing: SavedDrawing) {
  return {
    name: drawing.name,
    points: drawing.points,
    // Optional fields forwarded only when present — keeps the blob lean and
    // legacy-compatible (the record is a passthrough JSON store).
    ...(drawing.styles !== undefined ? { styles: drawing.styles } : {}),
    ...(drawing.visible !== undefined ? { visible: drawing.visible } : {}),
    ...(drawing.lock !== undefined ? { lock: drawing.lock } : {}),
    ...(drawing.extendData !== undefined ? { extendData: drawing.extendData } : {}),
  }
}

export async function saveDrawing(
  namespace: string,
  ticker: string,
  drawing: SavedDrawing,
): Promise<void> {
  const assetId = makeDrawingAssetId(ticker, drawing.syncId)
  const path = chartAssetPath(namespace, 'drawing', assetId)
  recordDump(namespace, 'saveDrawing', path, { ticker, drawing })
  await usersClient.put(path, drawingBody(drawing))
}

/**
 * Tab-close drain: the same PUT / DELETE as a keepalive write, which the browser
 * lets outlive the unload (an ordinary fetch is cancelled with the page).
 * Fire-and-forget and never throws; `drawing: null` means the drawing was
 * deleted. Both verbs are idempotent, so re-sending a write that also lands
 * normally is harmless.
 */
export function beaconDrawing(
  namespace: string,
  ticker: string,
  syncId: string,
  drawing: SavedDrawing | null,
): void {
  const path = chartAssetPath(namespace, 'drawing', makeDrawingAssetId(ticker, syncId))
  void usersClient.beacon(path, drawing ? drawingBody(drawing) : undefined, {
    method: drawing ? 'PUT' : 'DELETE',
  })
}

export async function deleteDrawing(
  namespace: string,
  ticker: string,
  syncId: string,
): Promise<void> {
  const assetId = makeDrawingAssetId(ticker, syncId)
  const path = chartAssetPath(namespace, 'drawing', assetId)
  recordDump(namespace, 'deleteDrawing', path, { ticker, syncId })
  await usersClient.delete(path)
}

// ─── Chart settings profiles ───

export interface ChartSettingsProfileRecord<T = Record<string, unknown>> {
  id: string
  name?: string
  settings?: T
  createdAt?: string
  updatedAt?: string
}

export async function listChartSettingsProfiles<T = Record<string, unknown>>(
  namespace = GLOBAL_NAMESPACE,
): Promise<ChartSettingsProfileRecord<T>[]> {
  try {
    const result = await usersClient.get<ChartSettingsProfileRecord<T>[]>(
      chartAssetPath(namespace, 'chartSettingsProfile'),
    )
    return Array.isArray(result) ? result : []
  } catch {
    return []
  }
}

export async function loadChartSettingsProfile<T = Record<string, unknown>>(
  profileId: string,
  namespace = GLOBAL_NAMESPACE,
): Promise<ChartSettingsProfileRecord<T> | null> {
  try {
    return await usersClient.get<ChartSettingsProfileRecord<T>>(
      chartAssetPath(namespace, 'chartSettingsProfile', profileId),
    )
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

export async function saveChartSettingsProfile<T = Record<string, unknown>>(
  profileId: string,
  data: { name?: string; settings?: T },
  namespace = GLOBAL_NAMESPACE,
): Promise<ChartSettingsProfileRecord<T>> {
  const path = chartAssetPath(namespace, 'chartSettingsProfile', profileId)
  recordDump(namespace, 'saveChartSettingsProfile', path, { profileId, ...data })
  return usersClient.put<ChartSettingsProfileRecord<T>>(path, data)
}

export async function deleteChartSettingsProfile(
  profileId: string,
  namespace = GLOBAL_NAMESPACE,
): Promise<void> {
  const path = chartAssetPath(namespace, 'chartSettingsProfile', profileId)
  recordDump(namespace, 'deleteChartSettingsProfile', path, { profileId })
  await usersClient.delete(path)
}

// ─── Chart preferences (global per-user) ───
//
// A single global record holding cross-chart personal preferences. Today just
// the favorite-timeframes list; structured as an object so other global prefs
// can join later without a new asset type. Stored under a fixed asset_id.

export interface ChartPreferences {
  /** Favorite-timeframe PeriodKeys (e.g. ['1m', '5m', '1H', '1D']). */
  favoriteTimeframes: string[]
  /**
   * Favorite drawing tools (overlay names, e.g. ['rayLine', 'rect']): the
   * floating drawing rail's shortlist. Absent on load means the user has
   * never touched it (the store seeds a default); `[]` means they un-starred
   * everything and want it empty.
   */
  favoriteDrawingTools?: string[]
  /**
   * The tool wheel's layout (overlay names, clockwise from 12 o clock): one
   * layout for the whole app. Absent means never customized; the store
   * renders the default wheel.
   */
  toolWheelSlots?: string[]
  /**
   * The news sidebar chart's timeframe (a PeriodKey, e.g. '1D'). Absent means
   * never picked; the store opens on its default.
   */
  newsChartPeriod?: string
}

const CHART_PREFERENCES_ID = 'default'

const EMPTY_PREFERENCES: ChartPreferences = { favoriteTimeframes: [] }

export async function loadChartPreferences(): Promise<ChartPreferences> {
  try {
    const record = await usersClient.get<Partial<ChartPreferences>>(
      chartAssetPath(GLOBAL_NAMESPACE, 'chartPreferences', CHART_PREFERENCES_ID),
    )
    return {
      favoriteTimeframes: Array.isArray(record?.favoriteTimeframes)
        ? record.favoriteTimeframes.map(String)
        : [],
      ...(Array.isArray(record?.favoriteDrawingTools)
        ? { favoriteDrawingTools: record.favoriteDrawingTools.map(String) }
        : {}),
      ...(Array.isArray(record?.toolWheelSlots)
        ? { toolWheelSlots: record.toolWheelSlots.map(String) }
        : {}),
      ...(typeof record?.newsChartPeriod === 'string'
        ? { newsChartPeriod: record.newsChartPeriod }
        : {}),
    }
  } catch (err) {
    // First use (no record yet) → empty prefs, not an error.
    if (err instanceof ApiError && err.status === 404) return EMPTY_PREFERENCES
    throw err
  }
}

/**
 * PUTs the WHOLE record, so a caller must send every field it wants kept:
 * `Required` makes a save that forgets one list a type error, not a wipe.
 */
export async function saveChartPreferences(prefs: Required<ChartPreferences>): Promise<void> {
  const path = chartAssetPath(GLOBAL_NAMESPACE, 'chartPreferences', CHART_PREFERENCES_ID)
  recordDump(GLOBAL_NAMESPACE, 'saveChartPreferences', path, prefs)
  await usersClient.put(path, prefs)
}
